import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

const MaintenancePage = () => {
  const { t } = useTranslation('global');
  const [countdown, setCountdown] = useState(30); // 30 second countdown
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(new Date());
  const [checkCount, setCheckCount] = useState(0);
  
  // Function to check if maintenance is over
  const checkMaintenanceStatus = async () => {
    if (isChecking) return;
    
    setIsChecking(true);
    setCheckCount(prev => prev + 1);
    
    try {
      const response = await fetch('/api/health', {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.status !== 503) {
        // Maintenance is over - clear flag and redirect
        localStorage.removeItem('maintenance_mode');
        localStorage.removeItem('return_url');
        
        // Redirect to previous page or home
        const returnUrl = localStorage.getItem('return_url') || '/';
        window.location.href = returnUrl;
      } else {
        // Still in maintenance, reset countdown
        setCountdown(30);
        setLastChecked(new Date());
      }
    } catch (error) {
      console.log('Unable to check status:', error);
      // Keep trying - reset countdown
      setCountdown(30);
      setLastChecked(new Date());
    } finally {
      setIsChecking(false);
    }
  };
  
  // Auto-refresh countdown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Countdown finished, check status
      checkMaintenanceStatus();
    }
  }, [countdown]);
  
  // Format time nicely
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Manual refresh button handler
  const handleManualRefresh = () => {
    setCountdown(0); // Trigger immediate check
  };
  
  // Format last checked time
  const formattedLastChecked = lastChecked.toLocaleTimeString(t('maintenance.locale') || 'en', {
    hour: '2-digit',
    minute: '2-digit'
  });
  
  return (
    <div className={styles.maintenancePage}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <div className={styles.toolsIcon}>
              <span className={`${styles.wrench} ${styles.icon}`}>🔧</span>
              <span className={`${styles.screwdriver} ${styles.icon}`}>🛠️</span>
              <span className={`${styles.gear} ${styles.icon}`}>⚙️</span>
            </div>
          </div>
          
          <h1 className={styles.title}>
            {t('maintenance.title')}
          </h1>
          
          <p className={styles.message}>
            {t('maintenance.message')}
          </p>
          
         
          
          <div className={styles.details}>
            <div className={styles.detailItem}>
              <span className={`${styles.detailIcon} ${styles.icon}`}>⏱️</span>
              <div className={styles.detailText}>
                <strong>{t('maintenance.estimatedTime')}:</strong>
                <span>{t('maintenance.backInHours')}</span>
              </div>
            </div>
            
            <div className={styles.detailItem}>
              <span className={`${styles.detailIcon} ${styles.icon}`}>📅</span>
              <div className={styles.detailText}>
                <strong>{t('maintenance.scheduled')}:</strong>
                <span>{new Date().toLocaleDateString(t('maintenance.locale'), { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
            </div>
            
            <div className={styles.detailItem}>
              <span className={`${styles.detailIcon} ${styles.icon}`}>📧</span>
              <div className={styles.detailText}>
                <strong>{t('maintenance.needHelp')}:</strong>
                <span>{t('maintenance.contactEmail', { email: 'service.clients@chooseandwork.com' })}</span>
              </div>
            </div>
          </div>
          
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div className={styles.progressFill}></div>
            </div>
            <div className={styles.progressText}>
              {t('maintenance.progress')}
            </div>
          </div>
          
          <div className={styles.actionButtons}>
            <button 
              className={`${styles.refreshButton} ${styles.primaryButton}`}
              onClick={handleManualRefresh}
              disabled={isChecking}
            >
              {isChecking 
                ? (t('maintenance.checking') || 'Checking...') 
                : (t('maintenance.refreshButton') || 'Check Now')
              }
            </button>
            <button 
              className={`${styles.homeButton} ${styles.secondaryButton}`}
              onClick={() => window.location.href = '/'}
            >
              {t('maintenance.homeButton')}
            </button>
          </div>
          
          <p className={styles.thankYou}>
            {t('maintenance.thankYou')}
          </p>
        </div>
      </div>
    </div>
  );
}

export default MaintenancePage;